/**
 * JWT Decode Utilities
 * For decoding JWT tokens to check expiration time
 */

interface JWTPayload {
  userId: string
  exp: number // Unix timestamp in seconds
  iat: number
}

/**
 * Decode a JWT token without verification
 * Only for reading claims, not for security validation
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(base64))
    return payload
  } catch {
    return null
  }
}

/**
 * Get the expiration time of a JWT token as a Date
 */
export function getTokenExpirationTime(token: string): Date | null {
  const payload = decodeJWT(token)
  if (!payload?.exp) return null
  return new Date(payload.exp * 1000)
}

/**
 * Get time until token expiry in milliseconds
 * Returns null if token is invalid or already expired
 */
export function getTimeUntilExpiry(token: string): number | null {
  const expTime = getTokenExpirationTime(token)
  if (!expTime) return null
  const remaining = expTime.getTime() - Date.now()
  return remaining > 0 ? remaining : null
}

/**
 * Check if a token is expired
 */
export function isTokenExpired(token: string): boolean {
  const timeUntilExpiry = getTimeUntilExpiry(token)
  return timeUntilExpiry === null || timeUntilExpiry <= 0
}
